import type { ClassDefinition } from '../../schemas/classDefinition';
import { druidoAdvanced2to5Moves } from '../moves/druido/advanced-2-5';
import { druidoAdvanced6to10Moves } from '../moves/druido/advanced-6-10';
import { druidoStartingAutoMoves, druidoStartingChoiceMoves } from '../moves/druido/initial';

/**
 * Definizione della classe **Druido** (manuale V1, rr. 2962-3318).
 *
 * Sesta classe trascritta. **Casteggiante condizionato**: il PG è
 * casteggiante solo se sceglie *Divinità della Natura* nel gruppo iniziale
 * (alternativa: *Mutaforma* — niente casting). Pattern nuovo nel sistema
 * `services/spellAccess.ts`: la mossa `druido.divinita-della-natura` è il
 * **trigger del casting**, non una mossa separata di tipo "lanciare
 * incantesimi".
 *
 * Pattern principali stress-testati:
 * - **Casteggiante condizionato** (D2 della PR): la spell-list
 *   `druido-native` si attiva solo se il PG ha `druido.divinita-della-natura`
 *   tra le sue mosse. Il PG con *Mutaforma* vede il placeholder.
 * - **Mossa-config *Nato dalla Terra*** con 10 opzioni Terra strutturate
 *   (`MoveSchema.choices`, choiceCount 1) + Segno Rivelatore in description.
 * - **`prerequisites.allOf`** su mosse advanced (Magia Fatata, Il Folletto).
 * - **Tradizione Sacra implicita "della Natura"**: `tradizioni: ['natura']`
 *   (1 sola, non discriminata in `ActiveSpellList`).
 *
 * **Punti notevoli**:
 * - Dado di danno **d6**, PF base 7, Carico base 7
 * - 5 archetipi razza-tematici
 * - 4 mosse iniziali automatiche + 2 mosse iniziali di scelta (`pickCount: 1`)
 * - 16 mosse avanzate L2-5, 14 mosse avanzate L6-10 (41 mosse totali)
 * - 4 template di Legami (= `maxBonds`, FI-3 rispettato)
 * - **Casteggiante condizionato** alla scelta *Divinità della Natura*
 */
export const druidoClassDefinition: ClassDefinition = {
  id: 'druido',
  name: 'Druido',

  intro:
    "Il fuoco danza, proiettando ombre tremolanti sui volti di coloro che vi siedono attorno. Cosa ti ha condotto fin qui, in mezzo a questa gente che odora di città e di fatica? Un barlume di compassione? Un istinto protettivo, come quello di una madre per i suoi cuccioli? Sono forse loro la tua nuova famiglia, questo strano gruppo di compagni? Fratelli e sorelle inusuali, senza dubbio. Qualunque sia il legame che vi unisce, senza la tua profonda connessione con la natura e le sue forze, sarebbero prede facili.\n\nIl tuo cuore appartiene agli spazi sconfinati, ai luoghi sacri dove la terra respira. Sei nato dalla sua essenza e porti il sigillo dei suoi spiriti inciso sulla pelle. Non solo la forza indomita della natura, ma anche la sua intrinseca armonia si manifesta in te, un equilibrio che cerchi di mantenere in te stesso e nel mondo. Forse un tempo eri come loro, un abitante della città che ora hai abbandonato. Ma quella vita è un ricordo sbiadito, un guscio vuoto.\n\nAscolta le loro invocazioni a dèi di pietra, osservali mentre lucidano le loro effigi d'argento. Sognano la gloria che troveranno in quella città ormai lontana, una città che marcisce nel ricordo. I loro dèi sono giovani e inesperti, la loro fede è fragile come vetro. Tu, invece, segui le antiche vie, comunicando con gli spiriti e attingendo a fonti di potere più profonde. Che sia l'incontrollabile furia degli elementi a rispondere al tuo tocco, o la sottile e incantevole magia dei luoghi tra i mondi. Prenderai la tua parte del tesoro, senza dubbio, ma ti sentirai mai veramente uno di loro? Solo il tempo potrà dirlo.",

  // Manuale V1, rr. 2973-2979
  suggestedNames: [
    // Umano
    'Elana',
    'Obelis',
    'Herran',
    'Syla',
    'Andanna',
    'Siobhan',
    'Aziz',
    'Pelin',
    'Sibel',
    'Nils',
    'Wei',
    // Elfo
    'Hycorax',
    'Ethanwe',
    'Sinathel',
    'Demanor',
    'Menoliir',
    'Mithralan',
    'Taeros',
    'Aegor',
    // Nano
    'Rinkha',
    'Moikhaz',
    'Funthor',
    'Argtol',
    'Ralkha',
    'Gadra',
    'Thrilmur',
    'Thea',
    'Clornbel',
    // Halfling
    'Tanner',
    'Dunstan',
    'Rose',
    'Ivy',
    'Robard',
    'Mab',
    'Bartholomew',
    'Puck',
    'Anne',
    // Mezz'orco
    'Durga',
    'Heda',
    'Lurzzu',
    'Groga',
    'Sharkja',
    'Nazmuz',
    'Glugva',
    'Buzdogga',
    'Pruk',
  ],

  suggestedTitles: [],

  // Manuale V1, rr. 2981-2987
  aspetti: [
    {
      label: 'Occhi',
      options: ['Saggi', 'Selvaggi', 'Incalzanti'],
    },
    {
      label: 'Capelli',
      options: ['Cappuccio di pelliccia', 'Capelli disordinati', 'Trecce'],
    },
    {
      label: 'Abiti',
      options: ['Abiti cerimoniali', 'Comode vesti di cuoio', 'Pelli consunte'],
    },
    {
      label: 'Corpo',
      options: ['Emaciato', 'Massiccio', 'Flessuoso'],
    },
  ],

  // Manuale V1, rr. 2989-2995
  damageDie: 'd6',
  baseHp: 7,
  baseLoad: 7,

  // Manuale V1, rr. 2997-3003
  alignmentPrecepts: {
    buono: 'Aiuti qualcuno o qualcosa a crescere.',
    legale:
      'Non accetti un compromesso, pur di restare fedele alla volontà degli spiriti della natura.',
    neutrale: 'Elimini una minaccia sovrannaturale.',
    caotico: 'Distruggi un simbolo della civiltà.',
    malvagio: 'Perverti la natura per il tuo tornaconto personale.',
  },

  // Manuale V1, rr. 3029-3033. **maxBonds: 4** (4 templates ↔ 4, FI-3 rispettato).
  bondTemplates: [
    '_____ odora più di preda che di cacciatore. Cosa devo fare con lui?',
    'Gli spiriti mi hanno detto di un grande pericolo che perseguita _____.',
    'Ho mostrato a _____ un rito segreto della mia Terra. Ne sarà degno?',
    '_____ ha assaggiato il mio sangue e io il suo. Siamo legati da questo gesto.',
  ],
  maxBonds: 4,

  // Manuale V1, rr. 3037-3045
  archetypes: [
    {
      id: 'umano',
      name: 'Umano',
      associatedRace: 'umano',
      description:
        "Anche se l'animale non proviene dalla tua Terra, o non ne hai *Studiato l'Essenza*, puoi comunque applicare *Mutaforma* e *Lingua degli Spiriti* a qualsiasi animale domestico.",
      grantedMoveId: 'druido.archetipo-umano',
    },
    {
      id: 'elfo',
      name: 'Elfo',
      associatedRace: 'elfo',
      description:
        'Considera sempre le Grandi Foreste come una tua Terra, in aggiunta a quella che scegli con *Nato dalla Terra*.',
      grantedMoveId: 'druido.archetipo-elfo',
      // Audit 1 (D11 follow-up): la Terra "Grandi Foreste" è auto-aggiunta
      // al pool del Druido-Elfo. Lo `choiceCount: 1` di `nato-dalla-terra`
      // resta invariato (il giocatore sceglie 1 Terra manualmente), ma il
      // pool effettivo del PG-Elfo include sempre anche 'grandi-foreste'.
      // Vedi `services/archetypeGrants.ts::getEffectiveSelectedChoices`.
      grantsMoveSelections: [{ moveId: 'druido.nato-dalla-terra', choiceIds: ['grandi-foreste'] }],
    },
    {
      id: 'nano',
      name: 'Nano',
      associatedRace: 'nano',
      description:
        'Quando prendi *Fratello del Cacciatore* o *Sorella del Predatore*, puoi scegliere anche tra le mosse dello Sciamano.',
      grantedMoveId: 'druido.archetipo-nano',
    },
    {
      id: 'halfling',
      name: 'Halfling',
      associatedRace: 'halfling',
      description:
        'Canti le canzoni risananti delle sorgenti e dei ruscelli. Quando vi *accampate*, al risveglio, tu e i tuoi alleati guarite +1d6.',
      grantedMoveId: 'druido.archetipo-halfling',
    },
    {
      id: 'mezzorco',
      name: "Mezz'orco",
      associatedRace: 'mezzorco',
      description:
        'Quando *parlamenti* con animali, spiriti della natura o esseri fatati, oppure *sfidi il pericolo* (con **CAR**) contro di essi, hai +1 al tiro. Con gente civilizzata, invece, subisci -1.',
      grantedMoveId: 'druido.archetipo-mezzorco',
    },
  ],

  startingMoves: druidoStartingAutoMoves.map((m) => m.id),

  // Manuale V1, r. 3088: "Inoltre, scegli una delle seguenti mosse"
  // *Divinità della Natura* abilita il casting (PG diventa casteggiante).
  // *Mutaforma* è alternativa non-casteggiante.
  startingChoiceGroups: [
    {
      id: 'mossa-iniziale-aggiuntiva',
      name: 'Mossa iniziale aggiuntiva',
      description:
        'Scegli una di queste due mosse. *Divinità della Natura* attiva il casting (Tradizione della Natura); *Mutaforma* è alternativa non casteggiante.',
      moveOptions: druidoStartingChoiceMoves.map((m) => m.id),
      pickCount: 1,
      expandableInAdvanced: false,
    },
  ],

  advancedMoves2to5: druidoAdvanced2to5Moves.map((m) => m.id),
  advancedMoves6to10: druidoAdvanced6to10Moves.map((m) => m.id),

  // Tradizione Sacra implicita "della Natura" (manuale r. 6200).
  // Il `castingMoveId` punta alla mossa-trigger `druido.divinita-della-natura`,
  // che il `services/spellAccess.ts` riconosce come trigger della spell-list
  // `druido-native`.
  // `manaMoveId` è semantico: il manuale (r. 3103) dice che *Divinità della
  // Natura* concede *Mana* del Chierico — qui usiamo l'ID `druido.mana`
  // come **placeholder** (la mossa Mana non è trascritta a parte per il
  // Druido nel data-entry attuale; verrà aggiunta come stub se servirà
  // nelle PR future per la modalità giocabile).
  spellcasting: {
    cantripLabel: 'Orazioni',
    castingMoveId: 'druido.divinita-della-natura',
    manaMoveId: 'druido.mana',
    tradizioni: ['natura'],
  },

  // Manuale V1, rr. 3006-3027
  startingEquipment: {
    always: [
      {
        id: 'ricordo-della-terra',
        name: 'Ricordo della tua Terra',
        weight: 0,
        tags: [],
        userDescriptionPrompt: 'Descrivilo a tua scelta.',
      },
    ],
    choices: [
      {
        id: 'difesa-iniziale',
        prompt: 'Scegli le tue difese',
        pickCount: 1,
        options: [
          {
            id: 'armatura-di-cuoio',
            label: 'Armatura di cuoio',
            items: [
              {
                id: 'armatura-di-cuoio',
                name: 'Armatura di cuoio',
                weight: 1,
                armor: 1,
                tags: ['indossato'],
              },
            ],
          },
          {
            id: 'scudo-di-legno',
            label: 'Scudo di legno',
            items: [
              {
                id: 'scudo-di-legno',
                name: 'Scudo di legno',
                weight: 2,
                armor: 1,
                tags: [],
                description: 'Quando usato.',
              },
            ],
          },
        ],
      },
      {
        id: 'arma-iniziale',
        prompt: "Scegli un'opzione per la tua arma",
        pickCount: 1,
        options: [
          {
            id: 'randello',
            label: 'Randello',
            items: [
              {
                id: 'randello',
                name: 'Randello',
                weight: 1,
                tags: ['media', 'stordente'],
              },
            ],
          },
          {
            id: 'bastone',
            label: 'Bastone',
            items: [
              {
                id: 'bastone',
                name: 'Bastone',
                weight: 1,
                tags: ['media', 'a due mani'],
              },
            ],
          },
          {
            id: 'lancia',
            label: 'Lancia',
            items: [
              {
                id: 'lancia',
                name: 'Lancia',
                weight: 1,
                tags: ['lunga', 'da lancio', 'vicino'],
              },
            ],
          },
        ],
      },
      {
        id: 'extra-iniziale',
        prompt: "Scegli un'opzione tra le seguenti",
        pickCount: 1,
        options: [
          {
            id: 'attrezzatura-da-avventuriero',
            label: 'Attrezzatura da Avventuriero',
            items: [
              {
                id: 'attrezzatura-da-avventuriero',
                name: 'Attrezzatura da Avventuriero',
                weight: 2,
                uses: 5,
                tags: [],
              },
            ],
          },
          {
            id: 'erbe-e-unguenti',
            label: 'Erbe e Unguenti',
            items: [
              {
                id: 'erbe-e-unguenti',
                name: 'Erbe e Unguenti',
                weight: 1,
                uses: 2,
                tags: ['lento'],
              },
            ],
          },
          {
            id: 'erba-pipa-halfling',
            label: 'Erba Pipa degli Halfling',
            items: [
              {
                id: 'erba-pipa-halfling',
                name: 'Erba Pipa degli Halfling',
                weight: 0,
                uses: 6,
                tags: [],
              },
            ],
          },
          {
            id: 'antitossina-x3',
            label: '3 Antitossina',
            items: [
              {
                id: 'antitossina',
                name: 'Antitossina',
                weight: 0,
                uses: 1,
                quantity: 3,
                tags: [],
              },
            ],
          },
        ],
      },
    ],
  },
};
